/**
 * Прогноз денег для трат по дням — один на всё приложение.
 *
 * До него будущее считали пять мест, и каждое по-своему. «Могу ли купить» и
 * сценарии советника шли по дням, но без кредиток. «После прихода» брал
 * кредитку, но не видел покупок после выписки и считал жизнь месяцем, а
 * доход — названной суммой. Раскладка о картах не знала вовсе, а запас хода
 * крутил ещё один свой цикл. У владельца 9 октября по выписке нужно внести
 * 149 374,93 ₽, и проверка покупки этого не видела: обещала больше правды.
 *
 * Здесь будущее собирается один раз — событиями по дням:
 *
 * · **поступления** — по ритму источника, осторожной оценкой
 *   (`conservativeIncome`). Сегодняшнее не прибавляется: оно могло ещё не
 *   прийти, а пришедшее уже сидит в остатке;
 * · **платежи** — неоплаченные наступления из общего календаря. Просроченные
 *   ложатся на сегодня: пока человек их не отметил, деньги заняты, — так же
 *   считает «Можно потратить»;
 * · **карты** — остаток текущей выписки в ближайший день платежа, а покупки
 *   после выписки — в день платежа по следующей. Выписка сама не обновляется:
 *   её человек вводит руками, поэтому следующая выводится здесь из долга и
 *   дней карты, а данные человека не переписываются;
 * · **жизнь** — обычные траты по дням: (нужды + свободные траты) / 30;
 * · **«а если»** — гипотетические траты проверки покупки и советника.
 *
 * Остаток всегда складывается из тех же строк, и это проверяется тестами:
 *
 * ```
 * конец = начало + поступления − платежи − карты − жизнь − «а если»
 * ```
 *
 * Экран, которому нужен срез — отрезок после прихода, платежи до горизонта,
 * запас хода, — берёт события отсюда, а не собирает свои.
 */
import { ZERO, add, clampPos, minM, share, sub, type Money } from './money.js';
import {
  addDays, dayWords, diffDays, isAfter, isBefore, nextOccurrence, nextOccurrenceInclusive,
  prevOccurrenceInclusive, type PlainDate,
} from './date.js';
import { creditState } from './credit.js';
import {
  conservativeIncome, factClosing, incomeOccurrences, type IncomeFact,
} from './income.js';
import { backedReserves, paymentInstances, unsettledInstances } from './instances.js';
import type { Account, CreditTerms, IncomeStream, Obligation, Reserve } from './types.js';

/** Вид события прогноза. Поступление прибавляется, остальное вычитается. */
export type ForecastKind = 'income' | 'payment' | 'card' | 'living' | 'extra';

/** Чем платёж по карте является: остатком выписки или покупками после неё. */
export interface CardPart {
  accountId: string;
  part: 'statement' | 'after_statement';
  /**
   * Выписка, в которую попали эти деньги.
   *
   * `null` — день выписки человек не называл. Тогда и дата платежа за
   * покупки после выписки — допущение, и прогноз говорит об этом в `limits`.
   */
  statementDate: PlainDate | null;
}

export interface ForecastEvent {
  kind: ForecastKind;
  /** День, когда деньги приходят или уходят. */
  date: PlainDate;
  label: string;
  /** Сумма события, всегда положительная. */
  amount: Money;
  /** Сколько из суммы уже отложено конвертами — где бы они ни лежали. */
  covered: Money;
  /**
   * Сколько из отложенного лежит на счетах не для трат.
   *
   * Эту часть платёж берёт не из денег для трат: конверт на накопительном
   * счёте закрывает платёж своими деньгами. Так же считает «Можно
   * потратить» — иначе прогноз и главная разошлись бы на сумму конверта.
   */
  coveredOutside: Money;
  /** Чьё событие: обязательство, счёт карты, источник дохода. */
  ref: string;
  /** Ключ наступления платежа — у обязательств. */
  instanceId?: string;
  /** Срок прошёл, а платёж не отмечен: деньги считаются занятыми сегодня. */
  overdue?: boolean;
  card?: CardPart;
}

/**
 * Гипотетическая трата: «а если купить, внести, заплатить картой».
 *
 * Рабочих данных не меняет — живёт только внутри одного прогноза.
 */
export interface ForecastExtra {
  date: PlainDate;
  amount: Money;
  label: string;
  /**
   * Покупка этой кредиткой.
   *
   * Деньги уходят не в день покупки, а в день платежа по выписке, в которую
   * покупка попадёт: 17 октября при выписке 16-го и платеже 9-го — это
   * 9 декабря.
   */
  card?: string;
  /**
   * Взнос на эту кредитку.
   *
   * Деньги уходят в день взноса, а ближайшие платежи по карте уменьшаются на
   * ту же сумму: иначе досрочный взнос считался бы дважды — сегодня и ещё
   * раз в день платежа.
   */
  paysCard?: string;
}

export interface CashForecastInput {
  today: PlainDate;
  /** Последний день прогноза, включительно. */
  to: PlainDate;
  accounts: readonly Account[];
  creditTerms: readonly CreditTerms[];
  incomeStreams: readonly IncomeStream[];
  obligations: readonly Obligation[];
  /** Конверты как записаны: прогноз сам урежет их до денег на своём счёте. */
  reserves?: readonly Reserve[];
  /** Обычные траты в месяц: повседневные нужды и свободные траты. */
  needBaseline: Money;
  wantBaseline: Money;
  /** С какого дня приложение ведёт учёт: граница просрочки платежей. */
  trackingStartedAt?: PlainDate;
  /** Приходы, которые случились: пришедшее раньше срока второй раз не ждём. */
  incomeFacts?: readonly IncomeFact[];
  extra?: readonly ForecastExtra[];
}

/** Суммы по видам: то, из чего складывается остаток. */
export interface ForecastTotals {
  income: Money;
  /** Обязательные платежи — из денег для трат. */
  payments: Money;
  /** Платежи по кредиткам — из денег для трат. */
  cards: Money;
  living: Money;
  extra: Money;
}

export interface ForecastDay extends ForecastTotals {
  date: PlainDate;
  /** Остаток денег для трат в конце дня. */
  balance: Money;
}

export interface Forecast {
  today: PlainDate;
  to: PlainDate;
  /** Деньги для трат сейчас: ликвидные счета, кроме кредиток. */
  start: Money;
  /** Все события по дате. */
  events: ForecastEvent[];
  /** Каждый день от сегодня до `to`. */
  days: ForecastDay[];
  totals: ForecastTotals;
  /** Остаток в последний день. */
  end: Money;
  /** Самый низкий остаток и его день: именно он отправляет в кредитку. */
  minBalance: Money;
  minBalanceDate: PlainDate;
  /** Чего прогноз не знает. Пересказ ответа не вправе эти строки потерять. */
  limits: string[];
}

/** Счёт с деньгами для трат: ликвидный, не в архиве, не кредитка. */
const isSpending = (a: Account): boolean => a.isLiquid && !a.archived && a.kind !== 'credit_card';

/** Деньги для трат сейчас — то, от чего идёт прогноз. */
export const spendingMoney = (accounts: readonly Account[]): Money =>
  add(...accounts.filter(isSpending).map((a) => a.balance));

/**
 * Обычная жизнь в день.
 *
 * Одно определение на всё приложение: проверка покупки, темп, запас хода и
 * «После прихода» делят месяц на тридцать одинаково, иначе один и тот же
 * месяц жизни стоил бы на соседних экранах по-разному.
 */
export const livingPerDay = (need: Money, want: Money): Money => share(add(need, want), 1 / 30);

/** Сколько событие забирает из денег для трат. Поступление — ноль. */
export const fromSpending = (e: ForecastEvent): Money =>
  (e.kind === 'income' ? ZERO : sub(e.amount, e.coveredOutside));

/** Сколько ещё не отложено: то, что придётся найти к сроку. */
export const notCovered = (e: ForecastEvent): Money => clampPos(sub(e.amount, e.covered));

// ─────────────────────────────────────────────────────────────── карты

/** Когда придётся платить за покупку кредиткой. */
export interface CardPurchaseDue {
  /** Выписка, в которую попадёт покупка; `null` — день выписки неизвестен. */
  statementDate: PlainDate | null;
  /** День платежа по этой выписке. */
  paymentDate: PlainDate;
  /** Дней от покупки до платежа. */
  days: number;
  /** Платёж укладывается в беспроцентный срок. */
  withinGrace: boolean;
}

/**
 * Покупка кредиткой: в какую выписку попадёт и когда за неё платить.
 *
 * Покупка в день выписки считается попавшей в неё: так платить придётся
 * раньше, а ошибка в эту сторону не отправит человека на проценты.
 *
 * Без дня выписки срок — день платежа, следующий за ближайшим: позже выписка
 * с месячным циклом прийти не может, а «ближайший» для покупки после
 * выписки неверен почти всегда. Это допущение, и прогноз его называет.
 */
export function cardPurchaseDue(terms: CreditTerms, date: PlainDate): CardPurchaseDue {
  const statementDate = terms.statementDay
    ? nextOccurrenceInclusive(date, terms.statementDay)
    : null;
  const paymentDate = statementDate
    ? nextOccurrence(statementDate, terms.paymentDay)
    : nextOccurrence(nextOccurrenceInclusive(date, terms.paymentDay), terms.paymentDay);
  const days = diffDays(date, paymentDate);
  return { statementDate, paymentDate, days, withinGrace: days <= terms.graceDays };
}

export interface CardPaymentsInput {
  today: PlainDate;
  accounts: readonly Account[];
  creditTerms: readonly CreditTerms[];
  /** Конверты, обеспеченные деньгами (`backedReserves`). */
  reserves?: readonly Reserve[];
}

/**
 * Платежи по кредиткам, которые следуют из долга и условий карты.
 *
 * Два платежа, а не один:
 *
 * · **остаток выписки** — по выписке минус уже внесённое, в ближайший день
 *   платежа. Это то же число, что «по выписке внести» на экране карты;
 * · **покупки после выписки** — долг минус остаток выписки. Они попадут в
 *   следующую выписку и оплачиваются в день платежа после неё. У владельца
 *   на 30 сентября это 50 217,61 ₽ к 9 ноября, и прежде их не видел никто.
 *
 * Конверт под карту закрывает сначала ближний платёж: деньги нельзя
 * закрепить дважды, а срок остатка выписки наступает раньше.
 */
export function cardPayments(input: CardPaymentsInput): ForecastEvent[] {
  const out: ForecastEvent[] = [];
  const reserves = input.reserves ?? [];
  const spending = new Set(input.accounts.filter(isSpending).map((a) => a.id));

  for (const a of input.accounts) {
    if (a.kind !== 'credit_card' || a.archived) continue;
    const t = input.creditTerms.find((x) => x.accountId === a.id);
    if (!t) continue;
    const st = creditState(a, t, input.today);
    // `missing` у состояния карты — остаток выписки без учёта конвертов:
    // конверты разбираются здесь, по порядку сроков.
    const statement = st.missing;
    const after = clampPos(sub(st.debt, statement));

    const mine = reserves.filter((r) => r.forKind === 'credit' && r.forId === a.id);
    let pool = add(...mine.map((r) => r.amount));
    let outside = add(...mine.filter((r) => !spending.has(r.accountId)).map((r) => r.amount));
    const push = (part: CardPart['part'], date: PlainDate, statementDate: PlainDate | null, amount: Money) => {
      const covered = minM(pool, amount);
      pool = sub(pool, covered);
      const coveredOutside = minM(outside, covered);
      outside = sub(outside, coveredOutside);
      out.push({
        kind: 'card', date, amount, covered, coveredOutside, ref: a.id,
        label: part === 'statement' ? a.name : `${a.name}: покупки после выписки`,
        card: { accountId: a.id, part, statementDate },
      });
    };

    if (statement > ZERO) {
      // Выписка, которую оплачивает ближайший платёж, — последняя перед ним.
      const statementDate = t.statementDay
        ? prevOccurrenceInclusive(addDays(st.paymentDate, -1), t.statementDay)
        : null;
      push('statement', st.paymentDate, statementDate, statement);
    }
    if (after > ZERO) {
      // Покупки после выписки уходят в банк так же, как покупка сегодня:
      // в ближайшую выписку и в платёж после неё.
      const due = cardPurchaseDue(t, input.today);
      push('after_statement', due.paymentDate, due.statementDate, after);
    }
  }

  return out.sort((x, y) => x.date.localeCompare(y.date));
}

// ─────────────────────────────────────────────────────────────── прогноз

const KIND_ORDER: Record<ForecastKind, number> = {
  income: 0, payment: 1, card: 2, extra: 3, living: 4,
};

const byDateAndKind = (a: ForecastEvent, b: ForecastEvent): number =>
  (a.date === b.date ? KIND_ORDER[a.kind] - KIND_ORDER[b.kind] : a.date.localeCompare(b.date));

/**
 * Прогноз от сегодня до `to` включительно.
 *
 * Шаг — сутки: месячная гранулярность прячет провалы внутри месяца, а именно
 * они и отправляют человека в кредитку. Внутри дня порядок не важен: остаток
 * сравнивается в конце дня, и зарплата утром оплачивает покупку вечером.
 *
 * Сегодня — это «сейчас»: остаток на счетах уже учитывает случившееся.
 * Поэтому сегодняшний приход не прибавляется (мог ещё не прийти), день жизни
 * не списывается (частично прожит), а платежи и покупки сегодняшнего дня —
 * списываются: до конца дня они не сделаны.
 */
export function forecast(input: CashForecastInput): Forecast {
  const { today } = input;
  const to = isBefore(input.to, today) ? today : input.to;
  const tomorrow = addDays(today, 1);
  const backed = backedReserves({
    accounts: input.accounts, reserves: input.reserves ?? [],
    obligations: input.obligations, today,
  });
  const spending = new Set(input.accounts.filter(isSpending).map((a) => a.id));
  const start = spendingMoney(input.accounts);
  const events: ForecastEvent[] = [];
  const limits: string[] = [];

  // 1. Поступления. Факт, пришедший раньше срока, закрывает наступление:
  //    эти деньги уже на счёте.
  const facts = (input.incomeFacts ?? []).filter((f) => !isAfter(f.date, today));
  for (const s of input.incomeStreams) {
    for (const date of incomeOccurrences(s, tomorrow, to)) {
      if (factClosing(facts, s.id, date)) continue;
      events.push({
        kind: 'income', date, label: s.name, amount: conservativeIncome(s),
        covered: ZERO, coveredOutside: ZERO, ref: s.id,
      });
    }
  }

  // 2. Обязательные платежи: неурегулированное прошлое — сегодня, дальше —
  //    по календарю. Конверт под обязательство расходуется по порядку сроков:
  //    отложенное под просроченный платёж не может покрыть следующий.
  const overdue = unsettledInstances({
    obligations: input.obligations,
    reserves: backed,
    today,
    ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
  }).filter((i) => isBefore(i.scheduledDate, today) && i.unpaid > ZERO);
  const ahead = paymentInstances({
    obligations: input.obligations, reserves: backed, today, from: today, to,
  }).filter((i) => !i.settled && i.unpaid > ZERO);

  const pools = new Map<string, { all: Money; outside: Money }>();
  const poolOf = (obligationId: string) => {
    let p = pools.get(obligationId);
    if (!p) {
      const mine = backed.filter((r) => r.forKind === 'obligation' && r.forId === obligationId);
      p = {
        all: add(...mine.map((r) => r.amount)),
        outside: add(...mine.filter((r) => !spending.has(r.accountId)).map((r) => r.amount)),
      };
      pools.set(obligationId, p);
    }
    return p;
  };
  for (const inst of [...overdue, ...ahead]) {
    const p = poolOf(inst.obligationId);
    const covered = minM(p.all, inst.unpaid);
    p.all = sub(p.all, covered);
    const coveredOutside = minM(p.outside, covered);
    p.outside = sub(p.outside, coveredOutside);
    const late = isBefore(inst.scheduledDate, today);
    events.push({
      kind: 'payment',
      date: late ? today : inst.scheduledDate,
      label: inst.label,
      amount: inst.unpaid,
      covered,
      coveredOutside,
      ref: inst.obligationId,
      instanceId: inst.id,
      ...(late ? { overdue: true } : {}),
    });
  }

  // 3. Кредитки: остаток выписки и покупки после неё.
  const cards = cardPayments({
    today, accounts: input.accounts, creditTerms: input.creditTerms, reserves: backed,
  });

  const cardName = (id: string): string => input.accounts.find((a) => a.id === id)?.name ?? 'карте';

  // 4. «А если». Взнос на карту уменьшает её ближайшие платежи на ту же
  //    сумму — иначе деньги ушли бы дважды.
  for (const x of input.extra ?? []) {
    const when = isBefore(x.date, today) ? today : x.date;
    let date = when;
    if (x.card) {
      const t = input.creditTerms.find((c) => c.accountId === x.card);
      if (t) {
        const due = cardPurchaseDue(t, when);
        date = due.paymentDate;
        if (!due.statementDate) {
          limits.push(`День выписки по «${cardName(x.card)}» не назван — покупку картой считаю к платежу ${dayWords(date, today)}`);
        }
      }
    }
    if (!isAfter(date, to)) {
      events.push({
        kind: 'extra', date, label: x.label, amount: x.amount,
        covered: ZERO, coveredOutside: ZERO, ref: x.card ?? x.paysCard ?? 'extra',
      });
    }
    if (x.paysCard) {
      let left = x.amount;
      for (const c of cards) {
        if (left <= ZERO) break;
        if (c.ref !== x.paysCard || isBefore(c.date, when)) continue;
        const cut = minM(left, c.amount);
        left = sub(left, cut);
        c.amount = sub(c.amount, cut);
        c.covered = minM(c.covered, c.amount);
        c.coveredOutside = minM(c.coveredOutside, c.covered);
      }
    }
  }
  for (const c of cards) {
    if (c.amount <= ZERO || isAfter(c.date, to)) continue;
    events.push(c);
    if (c.card?.part === 'after_statement' && !c.card.statementDate) {
      limits.push(`День выписки по «${cardName(c.ref)}» не назван — покупки после выписки считаю к платежу ${dayWords(c.date, today)}`);
    }
  }

  // 5. Обычная жизнь — со следующего дня: сегодняшний частично прожит.
  const perDay = livingPerDay(input.needBaseline, input.wantBaseline);
  if (perDay > ZERO) {
    for (let d = tomorrow; !isAfter(d, to); d = addDays(d, 1)) {
      events.push({
        kind: 'living', date: d, label: 'Обычная жизнь', amount: perDay,
        covered: ZERO, coveredOutside: ZERO, ref: 'living',
      });
    }
  }

  events.sort(byDateAndKind);

  // Остаток по дням. Минимум — по концу дня: сегодня только тратится, так что
  // конец сегодняшнего дня не выше исходного остатка.
  const days: ForecastDay[] = [];
  let balance = start;
  let k = 0;
  for (let d = today; !isAfter(d, to); d = addDays(d, 1)) {
    const day: ForecastDay = {
      date: d, income: ZERO, payments: ZERO, cards: ZERO, living: ZERO, extra: ZERO, balance,
    };
    for (; k < events.length && events[k]!.date === d; k += 1) {
      const e = events[k]!;
      const m = fromSpending(e);
      if (e.kind === 'income') day.income = add(day.income, e.amount);
      else if (e.kind === 'payment') day.payments = add(day.payments, m);
      else if (e.kind === 'card') day.cards = add(day.cards, m);
      else if (e.kind === 'living') day.living = add(day.living, m);
      else day.extra = add(day.extra, m);
    }
    balance = sub(add(balance, day.income), add(day.payments, day.cards, day.living, day.extra));
    day.balance = balance;
    days.push(day);
  }

  const totals = totalsOf(days);
  let min = days[0]!;
  for (const d of days) if (d.balance < min.balance) min = d;

  return {
    today, to, start, events, days, totals,
    end: balance,
    minBalance: min.balance,
    minBalanceDate: min.date,
    limits: [...new Set(limits)],
  };
}

/** Суммы по видам за дни прогноза. */
function totalsOf(days: readonly ForecastTotals[]): ForecastTotals {
  return {
    income: add(...days.map((d) => d.income)),
    payments: add(...days.map((d) => d.payments)),
    cards: add(...days.map((d) => d.cards)),
    living: add(...days.map((d) => d.living)),
    extra: add(...days.map((d) => d.extra)),
  };
}

/**
 * Остаток на конец дня.
 *
 * До сегодня — исходный остаток: прошлое уже в нём. После конца прогноза
 * ответа нет, и выдумывать его продолжением нельзя.
 */
export function balanceOn(f: Forecast, date: PlainDate): Money | null {
  if (isBefore(date, f.today)) return f.start;
  if (isAfter(date, f.to)) return null;
  return f.days[diffDays(f.today, date)]!.balance;
}

/** Суммы по видам за отрезок `[from, to]` включительно. */
export function totalsBetween(f: Forecast, from: PlainDate, to: PlainDate): ForecastTotals {
  return totalsOf(f.days.filter((d) => !isBefore(d.date, from) && !isAfter(d.date, to)));
}

/**
 * Самый низкий остаток, если в этот день уйдёт ещё и эта сумма.
 *
 * Считается по готовому прогнозу, без пересборки: проверка покупки ищет
 * безопасную дату перебором дней, и собирать прогноз заново на каждый день
 * незачем — покупка сдвигает остаток вниз с её дня и до конца.
 */
export function minWithOutflow(
  f: Forecast, date: PlainDate, amount: Money,
): { minBalance: Money; minBalanceDate: PlainDate } {
  let min: { minBalance: Money; minBalanceDate: PlainDate } | null = null;
  for (const d of f.days) {
    const b = isBefore(d.date, date) ? d.balance : sub(d.balance, amount);
    if (min === null || b < min.minBalance) min = { minBalance: b, minBalanceDate: d.date };
  }
  return min!;
}
